import React, { useMemo, useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Pencil, Trash2, Search, X, Globe } from 'lucide-react';
import { toast } from 'sonner';
import {
  useExpenseCategories,
  useCreateExpenseCategory,
  useUpdateExpenseCategory,
  useDeleteExpenseCategory,
} from '@/hooks/finance/useExpenseCategories';
import { useDepartments } from '@/hooks/finance/useDepartments';
import {
  useDepartmentCostPositions,
  useAssignCostPositionToDepartment,
  useRemoveCostPositionFromDepartment,
  useToggleCategoryGlobal,
} from '@/hooks/finance/useDepartmentCostPositions';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { generateNextCostPositionCode, isDuplicateCodeError } from '@/lib/cost-position-code';
import { BusinessUnitBadge } from '@/components/finance/BusinessUnitSelect';
import type { ExpenseCategory } from '@/types/database/expense-categories';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAllocationsByCategory,
  useSaveCostPositionAllocations,
} from '@/hooks/finance/useCostPositionAllocations';
import { BUAllocationEditor, AllocationValue, allocationsAreValid } from '@/components/finance/BUAllocationEditor';

type EditState = {
  id?: string;
  name: string;
  code: string;
  description: string;
  parent_id: string;
  gl_account_code: string;
  is_active: boolean;
  is_global: boolean;
  default_business_unit: BusinessUnit | '';
  department_ids: string[];
};

const empty: EditState = {
  name: '',
  code: '',
  description: '',
  parent_id: '',
  gl_account_code: '',
  is_active: true,
  is_global: false,
  default_business_unit: '',
  department_ids: [],
};

const CostPositionsManagementPage: React.FC = () => {
  const { user: profile } = useAuth();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'finance';

  const { data: categories, isLoading } = useExpenseCategories();
  const { data: departments } = useDepartments();
  const { data: deptMappings } = useDepartmentCostPositions();
  const createCategory = useCreateExpenseCategory();
  const updateCategory = useUpdateExpenseCategory();
  const deleteCategory = useDeleteExpenseCategory();
  const toggleGlobal = useToggleCategoryGlobal();
  const assignDept = useAssignCostPositionToDepartment();
  const removeDept = useRemoveCostPositionFromDepartment();

  const [search, setSearch] = useState('');
  const [filterBU, setFilterBU] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<EditState>(empty);
  const [allocations, setAllocations] = useState<AllocationValue[]>([]);
  const { allocationsByCategory } = useAllocationsByCategory();
  const saveAllocations = useSaveCostPositionAllocations();

  const costGroups = useMemo(
    () => (categories || []).filter((c) => c.category_type === 'cost_group'),
    [categories]
  );

  const deptsByPositionId = useMemo(() => {
    const m = new Map<string, { id: string; name: string; mappingId: string }[]>();
    (deptMappings || []).forEach((x: any) => {
      if (!x.category_id || !x.department) return;
      const arr = m.get(x.category_id) || [];
      arr.push({ id: x.department.id, name: x.department.name, mappingId: x.id });
      m.set(x.category_id, arr);
    });
    return m;
  }, [deptMappings]);

  const positions = useMemo(() => {
    if (!categories) return [];
    const byId = new Map(categories.map((c) => [c.id, c]));
    return categories
      .filter((c) => c.category_type === 'cost_position')
      .map((p) => {
        const grp = p.parent_id ? byId.get(p.parent_id) : undefined;
        const top = grp?.parent_id ? byId.get(grp.parent_id) : undefined;
        return { ...p, group_name: grp?.name ?? '—', top_name: top?.name ?? '—' };
      })
      .sort((a, b) =>
        a.top_name.localeCompare(b.top_name) ||
        a.group_name.localeCompare(b.group_name) ||
        a.name.localeCompare(b.name)
      );
  }, [categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return positions.filter((p) => {
      const bu = (p as any).default_business_unit as string | null;
      if (filterBU === 'unassigned' && bu) return false;
      if (filterBU !== 'all' && filterBU !== 'unassigned' && bu !== filterBU) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        p.group_name.toLowerCase().includes(q) ||
        p.top_name.toLowerCase().includes(q)
      );
    });
  }, [positions, search, filterBU]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: positions.length, unassigned: 0 };
    BUSINESS_UNITS.forEach((b) => (c[b.value] = 0));
    positions.forEach((p) => {
      const bu = (p as any).default_business_unit as string | null;
      if (!bu) c.unassigned++;
      else c[bu] = (c[bu] || 0) + 1;
    });
    return c;
  }, [positions]);

  const openNew = () => {
    setForm(empty);
    setAllocations([]);
    setDialogOpen(true);
  };

  const openEdit = (p: any) => {
    const depts = deptsByPositionId.get(p.id) || [];
    setForm({
      id: p.id,
      name: p.name,
      code: p.code,
      description: p.description || '',
      parent_id: p.parent_id || '',
      gl_account_code: p.gl_account_code || '',
      is_active: p.is_active ?? true,
      is_global: (p as any).is_global === true,
      default_business_unit: ((p as any).default_business_unit || '') as BusinessUnit | '',
      department_ids: depts.map((d) => d.id),
    });
    const existing = allocationsByCategory.get(p.id) || [];
    if (existing.length > 0) {
      setAllocations(
        existing.map((a) => ({ business_unit: a.business_unit, percentage: Number(a.percentage) }))
      );
    } else if ((p as any).default_business_unit) {
      setAllocations([
        { business_unit: (p as any).default_business_unit as BusinessUnit, percentage: 100 },
      ]);
    } else {
      setAllocations([]);
    }
    setDialogOpen(true);
  };

  const syncDepartments = async (categoryId: string, newDeptIds: string[]) => {
    const existing = deptsByPositionId.get(categoryId) || [];
    const existingIds = new Set(existing.map((d) => d.id));
    const newSet = new Set(newDeptIds);
    // Remove
    for (const ex of existing) {
      if (!newSet.has(ex.id)) {
        await removeDept.mutateAsync(ex.mappingId);
      }
    }
    // Add
    for (const id of newDeptIds) {
      if (!existingIds.has(id)) {
        await assignDept.mutateAsync({ category_id: categoryId, department_id: id });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.parent_id) {
      toast.error('Please select a Cost Group (parent).');
      return;
    }
    const parent = categories?.find((c) => c.id === form.parent_id);
    const grand = parent?.parent_id ? categories?.find((c) => c.id === parent.parent_id) : undefined;
    const cashFlow = parent?.cash_flow_type || grand?.cash_flow_type || null;

    if (!allocationsAreValid(allocations)) {
      toast.error('BU allocations must sum to 100% (or be empty).');
      return;
    }
    // Mirror the dominant BU into default_business_unit for back-compat & forms autofill
    const dominant =
      allocations.length > 0
        ? [...allocations].sort((a, b) => b.percentage - a.percentage)[0].business_unit
        : null;

    const payload: any = {
      name: form.name,
      code: form.code.toUpperCase(),
      description: form.description || null,
      parent_id: form.parent_id,
      gl_account_code: form.gl_account_code || null,
      is_active: form.is_active,
      is_global: form.is_global,
      category_type: 'cost_position',
      is_recordable: true,
      cash_flow_type: cashFlow,
      default_business_unit: dominant,
    };

    try {
      let id = form.id;
      if (id) {
        await updateCategory.mutateAsync({ id, ...payload });
      } else {
        // Auto-generate code if blank, ensuring uniqueness
        if (!payload.code) {
          const allCodes = (categories || []).map((c: any) => c.code).filter(Boolean) as string[];
          payload.code = generateNextCostPositionCode(parent!.code, allCodes);
        }
        const created = await createCategory.mutateAsync(payload);
        id = (created as any).id;
      }
      if (id) {
        await syncDepartments(id, form.department_ids);
        await saveAllocations.mutateAsync({
          category_id: id,
          allocations: allocations.filter((a) => a.percentage > 0),
        });
      }
      toast.success(form.id ? 'Cost position updated' : 'Cost position created');
      setDialogOpen(false);
      setForm(empty);
      setAllocations([]);
    } catch (err: any) {
      if (isDuplicateCodeError(err)) {
        const allCodes = (categories || []).map((c: any) => c.code).filter(Boolean) as string[];
        const suggestion = parent ? generateNextCostPositionCode(parent.code, [...allCodes, payload.code]) : '';
        toast.error(`Code "${payload.code}" already exists${suggestion ? `. Try ${suggestion}` : ''}.`);
      } else {
        toast.error(err?.message || 'Failed to save');
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory.mutateAsync(id);
      toast.success('Cost position deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  const toggleDeptInForm = (deptId: string) => {
    setForm((f) => ({
      ...f,
      department_ids: f.department_ids.includes(deptId)
        ? f.department_ids.filter((d) => d !== deptId)
        : [...f.department_ids, deptId],
    }));
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Cost Positions</h1>
            <p className="text-muted-foreground">
              Manage cost positions: create, edit, delete, and assign departments and a default business unit.
            </p>
          </div>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" /> New Cost Position
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Cost Positions</CardTitle>
            <CardDescription>
              {filtered.length} of {positions.length} cost positions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by name, code, group, or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Tabs value={filterBU} onValueChange={setFilterBU}>
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="unassigned">No BU ({counts.unassigned})</TabsTrigger>
                {BUSINESS_UNITS.map((b) => (
                  <TabsTrigger key={b.value} value={b.value}>
                    {b.label} ({counts[b.value] || 0})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cost Position</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Cost Group</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Departments</TableHead>
                      <TableHead>Business Unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => {
                      const bu = (p as any).default_business_unit as string | null;
                      const depts = deptsByPositionId.get(p.id) || [];
                      const isGlobal = (p as any).is_global === true;
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{p.code}</TableCell>
                          <TableCell>{p.group_name}</TableCell>
                          <TableCell>{p.top_name}</TableCell>
                          <TableCell>
                            {isGlobal ? (
                              <Badge variant="secondary" className="gap-1">
                                <Globe className="h-3 w-3" /> Global
                              </Badge>
                            ) : depts.length === 0 ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {depts.map((d) => (
                                  <Badge key={d.id} variant="outline">
                                    {d.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const allocs = allocationsByCategory.get(p.id) || [];
                              if (allocs.length === 0) {
                                return <BusinessUnitBadge value={bu ?? undefined} />;
                              }
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {allocs.map((a) => {
                                    const b = BUSINESS_UNITS.find((x) => x.value === a.business_unit);
                                    return (
                                      <Badge
                                        key={a.business_unit}
                                        variant="outline"
                                        className="text-xs"
                                        style={{ borderColor: b?.color, color: b?.color }}
                                      >
                                        {b?.label} {Number(a.percentage)}%
                                      </Badge>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {p.is_active ? (
                              <Badge variant="secondary">Active</Badge>
                            ) : (
                              <Badge variant="outline">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(p)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {isAdmin && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="icon" variant="ghost">
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete cost position?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      "{p.name}" will be permanently deleted. This may fail if it is referenced by existing expenses.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(p.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No cost positions match your filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setForm(empty); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Cost Position' : 'New Cost Position'}</DialogTitle>
            <DialogDescription>
              Cost positions are recordable categories used when creating expenses and payment requests.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Fuel"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., COGS-DEL-FUEL"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cost Group (Parent) *</Label>
              <Select
                value={form.parent_id || 'none'}
                onValueChange={(v) => setForm({ ...form, parent_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select cost group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>Select a cost group</SelectItem>
                  {costGroups.map((cg) => (
                    <SelectItem key={cg.id} value={cg.id}>
                      {cg.name} ({cg.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>

            <div className="space-y-2">
              <Label>GL Account Code</Label>
              <Input
                value={form.gl_account_code}
                onChange={(e) => setForm({ ...form, gl_account_code: e.target.value })}
                placeholder="e.g., 6100"
              />
            </div>

            <div className="border rounded-md p-3">
              <BUAllocationEditor value={allocations} onChange={setAllocations} />
              <p className="text-xs text-muted-foreground mt-2">
                Expenses on this cost position will be split across business units in the BU P&amp;L
                using these percentages. Leave empty to mark as unassigned.
              </p>
            </div>

            <div className="space-y-2 border rounded-md p-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Departments</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="is_global" className="text-xs text-muted-foreground">Global (all departments)</Label>
                  <Switch
                    id="is_global"
                    checked={form.is_global}
                    onCheckedChange={(v) => setForm({ ...form, is_global: v })}
                  />
                </div>
              </div>
              {form.is_global ? (
                <p className="text-xs text-muted-foreground">
                  This cost position will be available to all departments. Per-department assignments are ignored.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Pick which departments can use this cost position. Leave empty to restrict to admins only.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {(departments || []).map((d) => {
                      const active = form.department_ids.includes(d.id);
                      return (
                        <Badge
                          key={d.id}
                          variant={active ? 'default' : 'outline'}
                          className="cursor-pointer select-none"
                          onClick={() => toggleDeptInForm(d.id)}
                        >
                          {d.name}
                          {active && <X className="h-3 w-3 ml-1" />}
                        </Badge>
                      );
                    })}
                    {(departments || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">No departments yet.</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createCategory.isPending || updateCategory.isPending}>
                {form.id ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CostPositionsManagementPage;
